import styled, { css, RuleSet } from 'styled-components'

import { MarketShopOrderStatus } from '@/models'

export const OrderCard = styled.div<{
  $status: MarketShopOrderStatus
  $isBeingDragged?: boolean
  $isNewOrder?: boolean
  $isOverlay?: boolean
  $isPreview?: boolean
}>(({ theme, $status, $isBeingDragged, $isNewOrder, $isOverlay, $isPreview }) => {
  const baseStyles = css`
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: ${theme.border.radius['12']};
    background: ${theme.colors.white};
    cursor: pointer;
    user-select: none;
    touch-action: pan-y;
    -webkit-tap-highlight-color: transparent;

    ${
      $isOverlay &&
      css`
      cursor: grabbing;
      box-shadow:
        0 8px 28px -6px rgba(24, 39, 75, 0.12),
        0 18px 88px -4px rgba(24, 39, 75, 0.14);
    `
    }

    ${
      $isNewOrder &&
      css`
      border: ${theme.border.size['1']} solid ${theme.colors.orange500};
      background-color: ${theme.colors.orange200};
    `
    }

    ${
      $isPreview &&
      css`
      pointer-events: none;
    `
    }

    ${
      !$isPreview &&
      css`
      // NOTE: This border-size calc fixes different monitor/device resolution settings
      // to ensure correct card hidden state when column closed
      @container (max-width: calc(4rem + ${theme.border.size['1']})) {
        & {
          display: none;
        }
      }
    `
    }

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      // NOTE: border-radius for after must be the same as for card itself
      border-radius: ${theme.border.radius['12']};
      background-color: transparent;
      pointer-events: none;
      user-select: none;
      transition: background-color ${theme.transition.duration['sm']} ease-out;
    }
  `

  const draggedByTypeStyles: Record<MarketShopOrderStatus, RuleSet> = {
    awaiting_pickup: css`
      &::after {
        background-color: ${theme.colors.green200};
      }
    `,
    completed: css`
      &::after {
        background-color: ${theme.colors.gray400};
      }
    `,
    declined: css`
      &::after {
        background-color: ${theme.colors.red200};
      }
    `,
    in_progress: css`
      &::after {
        background-color: ${theme.colors.blue200};
      }
    `,
    new: css`
      &::after {
        background-color: ${theme.colors.orange200};
      }
    `,
  }

  return css`
    ${baseStyles}
    ${$isBeingDragged && !$isOverlay && draggedByTypeStyles[$status]}
  `
})

export const Header = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.space['8']};
    padding: ${theme.space['8']} ${theme.space['12']}
      calc(${theme.space['8']} - ${theme.border.size['1']});
    border-bottom: 1px solid ${theme.colors.gray300};
  `,
)

export const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space['12']};
    padding: ${theme.space['12']};
  `,
)

export const OrderedItems = styled.ul(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space['4']};

    & > li {
      display: flex;
      align-items: center;
    }
  `,
)

export const Infos = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space['8']};
  `,
)

export const Controls = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space['12']};
  `,
)
